<template>
  <div class="canvas-wrapper">
    <div class="canvas-container" ref="canvas-container"></div>
  </div>
</template>

<script>
import * as PIXI from "pixi.js";
import { Viewport } from "pixi-viewport";
// import { MultiColorReplaceFilter } from "@pixi/filter-multi-color-replace";

const ENABLE =
  localStorage.getItem("Authentication") &&
  JSON.parse(localStorage.getItem("Authentication")).complete == true
    ? true
    : false; // Enable user interactions

let TEXTURE_WIDTH = 1260;
let TEXTURE_HEIGHT = 1812;

// const hexes = [
//   0xce8a82, 0xd68f9f, 0x28f7ce, 0x405100, 0x7ac02f, 0x040c2e, 0xbe7ed2,
//   0x530c4c, 0x66cf39, 0x8a8a35, 0x674352, 0x7748f8, 0x1209cf, 0xe1015c,
//   0x2f4470, 0xdc81f6, 0x9cd515, 0x59abed, 0xe38d9f, 0x69ef7d, 0xa455cc,
//   0x109581, 0x313931, 0x458792, 0xfdf8cf, 0xda6dfa, 0x3ce897, 0xbaae4e,
//   0x279623, 0x13ebff, 0x09a60f, 0xc7dfe4, 0xdcda82, 0x8bc872, 0xfb2149,
//   0x4f3d7a, 0x4e78ed, 0x83c7aa, 0x4e2b60, 0xe22def, 0xe145a8, 0x894c42,
//   0xcc2994, 0xba5267, 0xf4cf9a, 0x38a09d, 0x51a5e2, 0xb8c76d, 0x20baac,
//   0x2cee46, 0xcb9372, 0xf91080, 0x0b5c07, 0xc76d7c, 0x0a8371, 0x3429a7,
//   0x7a2e9a, 0x0620f5, 0x5ee885, 0x00d529,
// ];

// const getSelected = () => {
//   let selected = JSON.parse(localStorage.getItem("visited"));
//   if (!selected) selected = [];
//   selected = selected.map((hex) => parseInt(hex, 16));
//   return selected;
// };

const saveSelected = (hex) => {
  const selectedJson = localStorage.getItem("visited");
  let selected = [];
  if (selectedJson) selected = JSON.parse(selectedJson);
  if (!selected.includes(hex)) {
    selected.push(hex);
    localStorage.setItem("visited", JSON.stringify(selected));
  }
};

// At the end of the chain, we get a hex code that we match
// to a polaroid number!
const processClick = (hex) => {
  const setActiveHex = new CustomEvent("setActiveHex", { detail: hex });
  document.dispatchEvent(setActiveHex);
  const setActivePolaroid = new CustomEvent("setActivePolaroid", {
    detail: hex,
  });
  document.dispatchEvent(setActivePolaroid);
};

const getColorIndicesForCoord = (x, y, width) => {
  const red = y * (width * 4) + x * 4;
  return [red, red + 1, red + 2, red + 3];
};

// Utility function to convert RGB to HEX
const rgbToHex = (r, g, b) => {
  if (r > 255 || g > 255 || b > 255) throw "Invalid color component";
  let hex = ((r << 16) | (g << 8) | b).toString(16);
  return hex.padStart(6, "0");
};

// Throttle function call (used for resize)
const throttle = (function () {
  let timer = 0;
  return function (callback, ms) {
    clearTimeout(timer);
    timer = setTimeout(callback, ms);
  };
})();

const init = (container) => {
  // Create new PIXI application
  const app = new PIXI.Application({
    autoResize: true,
    resolution: devicePixelRatio,
    autoDensity: true,
    width: container.clientWidth,
    height: container.clientHeight,
    autoStart: false,
  });
  app.renderer.backgroundColor = 0xdadada;
  container.appendChild(app.view);

  // Add PIXI viewport plugin
  const viewport = new Viewport({
    screenWidth: container.clientWidth,
    screenHeight: container.clientHeight,
    worldWidth: container.clientWidth,
    worldHight: container.clientHeight,
    interaction: app.renderer.plugins.interaction,
  });
  app.stage.addChild(viewport);

  window.addEventListener("resize", () => {
    throttle(() => {
      const parent = app.view.parentNode;
      viewport.resize(parent.clientWidth, parent.clientHeight);
      app.renderer.resize(parent.clientWidth, parent.clientHeight);
    }, 200);
  });

  // Add panning and inertia

  if (ENABLE) viewport.drag().decelerate({ friction: 0.75 });

  // Grid config
  const gridTextures = [
    [
      PIXI.Loader.shared.resources.grid1.texture,
      PIXI.Loader.shared.resources.grid7.texture,
      PIXI.Loader.shared.resources.grid13.texture,
    ],
    [
      PIXI.Loader.shared.resources.grid2.texture,
      PIXI.Loader.shared.resources.grid8.texture,
      PIXI.Loader.shared.resources.grid14.texture,
    ],
    [
      PIXI.Loader.shared.resources.grid3.texture,
      PIXI.Loader.shared.resources.grid9.texture,
      PIXI.Loader.shared.resources.grid15.texture,
    ],
    [
      PIXI.Loader.shared.resources.grid4.texture,
      PIXI.Loader.shared.resources.grid10.texture,
      PIXI.Loader.shared.resources.grid16.texture,
    ],
    [
      PIXI.Loader.shared.resources.grid5.texture,
      PIXI.Loader.shared.resources.grid11.texture,
      PIXI.Loader.shared.resources.grid17.texture,
    ],
    [
      PIXI.Loader.shared.resources.grid6.texture,
      PIXI.Loader.shared.resources.grid12.texture,
      PIXI.Loader.shared.resources.grid18.texture,
    ],
  ];

  const grid = gridTextures.map((row) => {
    return row.map((texture) => {
      const sprite = new PIXI.Sprite(texture);
      sprite.interactive = false;
      return sprite;
    });
  });

  const gridHexTextures = [
    [
      PIXI.Loader.shared.resources.gridhex1.texture,
      PIXI.Loader.shared.resources.gridhex7.texture,
      PIXI.Loader.shared.resources.gridhex13.texture,
    ],
    [
      PIXI.Loader.shared.resources.gridhex2.texture,
      PIXI.Loader.shared.resources.gridhex8.texture,
      PIXI.Loader.shared.resources.gridhex14.texture,
    ],
    [
      PIXI.Loader.shared.resources.gridhex3.texture,
      PIXI.Loader.shared.resources.gridhex9.texture,
      PIXI.Loader.shared.resources.gridhex15.texture,
    ],
    [
      PIXI.Loader.shared.resources.gridhex4.texture,
      PIXI.Loader.shared.resources.gridhex10.texture,
      PIXI.Loader.shared.resources.gridhex16.texture,
    ],
    [
      PIXI.Loader.shared.resources.gridhex5.texture,
      PIXI.Loader.shared.resources.gridhex11.texture,
      PIXI.Loader.shared.resources.gridhex17.texture,
    ],
    [
      PIXI.Loader.shared.resources.gridhex6.texture,
      PIXI.Loader.shared.resources.gridhex12.texture,
      PIXI.Loader.shared.resources.gridhex18.texture,
    ],
  ];

  const gridHex = gridHexTextures.map((row) => {
    return row.map((texture) => {
      const sprite = new PIXI.Sprite(texture);
      sprite.interactive = false;
      return sprite;
    });
  });

  // const gridOverlay = gridHexTextures.map((row) => {
  //   return row.map((texture) => {
  //     const sprite = new PIXI.Sprite(texture);
  //     sprite.interactive = false;
  //     return sprite;
  //   });
  // });

  const gridContainer = new PIXI.Container();
  gridContainer.interactive = false;
  const gridHexContainer = new PIXI.Container();
  gridHexContainer.interactive = false;
  // const visitedContainer = new PIXI.Container();
  // visitedContainer.interactive = false;

  const renderGrid = (initialGrid) => {
    // const selectedHexes = getSelected();

    gridContainer.removeChildren();
    gridHexContainer.removeChildren();
    viewport.removeChildren();
    // visitedContainer.removeChildren();

    const startX =
      Math.floor((-viewport.x - viewport.worldScreenWidth) / TEXTURE_WIDTH) *
      TEXTURE_WIDTH;

    const endX =
      Math.ceil((-viewport.x + viewport.worldScreenWidth * 2) / TEXTURE_WIDTH) *
      TEXTURE_WIDTH;

    const startY =
      Math.floor((-viewport.y - viewport.worldScreenHeight) / TEXTURE_HEIGHT) *
      TEXTURE_HEIGHT;

    let endY =
      Math.ceil(
        (-viewport.y + viewport.worldScreenHeight * 2) / TEXTURE_HEIGHT
      ) * TEXTURE_HEIGHT;

    let count = 0;
    let rows = 0;
    let cols = 0;
    let skip = false;
    let skipped = 0;

    for (let x = startX; x < endX; x += TEXTURE_WIDTH) {
      rows++;
      for (let y = startY; y < endY; y += TEXTURE_HEIGHT) {
        count++;
        cols++;

        skip =
          x < -viewport.x - TEXTURE_WIDTH ||
          x > -viewport.x + viewport.worldScreenWidth ||
          y < -viewport.y - TEXTURE_HEIGHT ||
          y > -viewport.y + viewport.worldScreenHeight
            ? true
            : false;

        if (skip && !initialGrid) {
          skipped++;
          continue;
        }

        let indexX = Math.abs(x / TEXTURE_WIDTH) % grid.length;
        let indexY = Math.abs(y / TEXTURE_HEIGHT) % grid[0].length;

        // THIS LOGIC WAS RIGHT IN THE END
        indexX = x < 0 ? 6 - indexX : indexX;
        indexY = y < 0 ? 3 - indexY : indexY;

        // HAD TO RESET THE X Y IF TOO BIG
        if (indexX == 6) indexX = 0;
        if (indexY == 3) indexY = 0;

        const sprite = grid[indexX][indexY];
        sprite.position.x = x;
        sprite.position.y = y;
        gridContainer.addChild(sprite);

        const hexSprite = gridHex[indexX][indexY];
        hexSprite.position.x = x;
        hexSprite.position.y = y;
        gridHexContainer.addChild(hexSprite);

        // const visitedSprite = gridOverlay[indexX][indexY];
        // visitedSprite.position.x = x;
        // visitedSprite.position.y = y;
        // visitedContainer.addChild(visitedSprite);
      }
    }

    viewport.addChild(gridContainer);
    // console.log('-----------GRID----------------', count, rows, cols/rows);

    // Is this efficient enough?
    // const hexesToOverlay = selectedHexes.map((hex) => [hex, 0xffffff]);
    // const hexesToIgnore = hexes
    //   .filter((hex) => !selectedHexes.includes(hex))
    //   .map((hex) => [hex, 0x000000]);

    // const overlayFilter = new MultiColorReplaceFilter(
    //   [...hexesToOverlay, ...hexesToIgnore],
    //   0.001
    // );
    // const alphaFilter = new PIXI.filters.AlphaFilter(0.5);
    // alphaFilter.blendMode = PIXI.BLEND_MODES.SCREEN;
    // visitedContainer.filters = [overlayFilter, alphaFilter];

    // viewport.addChild(visitedContainer);

    return {
      size: count,
      skipped: skipped,
      cols: rows,
      rows: cols / rows,
    };
  };

  const { cols, rows } = renderGrid(true);

  app.ticker.update();

  // just right screens
  let xcenter = gridContainer.width / 2;
  let ycenter = gridContainer.height / 2;

  //big screens
  if (cols > 6) xcenter -= (TEXTURE_WIDTH * (cols - 6)) / 2;
  if (rows > 3) ycenter -= (TEXTURE_HEIGHT * (rows - 3)) / 2;

  //small screens
  if (cols < 6) xcenter += (TEXTURE_WIDTH * (6 - cols)) / 2;
  if (rows < 3) ycenter += (TEXTURE_HEIGHT * (3 - rows)) / 2;

  // on small mobile the album poloroids needs moving up / left to be above the UI
  if (cols == 2 && rows == 2) {
    xcenter += 60;
    ycenter += 200;
  }

  // xcenter -= TEXTURE_WIDTH / 2
  // ycenter -= TEXTURE_HEIGHT / 2

  viewport.snap(xcenter, ycenter, {
    time: 0,
    removeOnComplete: true,
    removeOnInterrupt: true,
  });

  // Still loads a little too fast... add a second delay!
  setTimeout(() => {
    const loadedEvent = new CustomEvent("canvas-loaded");
    document.dispatchEvent(loadedEvent);
  }, 1000);

  viewport.on("moved", () => {
    renderGrid();
    app.ticker.update();
  });

  // on "click" or "touch"
  viewport.on("clicked", (e) => {
    if (!ENABLE) return null;

    // get the click position relative to the canvas
    const xClickPos = Math.floor(e.event.data.global.x);
    const yClickPos = Math.floor(e.event.data.global.y);

    viewport.addChild(gridHexContainer);

    const renderTexture = PIXI.RenderTexture.create(
      viewport.worldScreenWidth,
      viewport.worldScreenHeight
    );
    app.renderer.render(app.stage, renderTexture);

    viewport.removeChild(gridHexContainer);

    const pixels = app.renderer.extract.pixels(renderTexture);

    const colorIndices = getColorIndicesForCoord(
      xClickPos,
      yClickPos,
      viewport.worldScreenWidth
    );

    const pixelRgba = [
      pixels[Math.floor(colorIndices[0])],
      pixels[Math.floor(colorIndices[1])],
      pixels[Math.floor(colorIndices[2])],
    ];

    const hex = rgbToHex(pixelRgba[0], pixelRgba[1], pixelRgba[2]);

    processClick(hex);

    // save and rerender the grid to update
    // the overlays.
    saveSelected(hex);
    renderGrid();
    app.ticker.update();
  });
};

export default {
  mounted() {
    if (devicePixelRatio < 1.5) {
      PIXI.Loader.shared
        .add("grid1", "./textures/polaroids/grid_1-1_polaroid.jpg")
        .add("grid2", "./textures/polaroids/grid_1-2_polaroid.jpg")
        .add("grid3", "./textures/polaroids/grid_2-1_polaroid.jpg")
        .add("grid4", "./textures/polaroids/grid_2-2_polaroid.jpg")
        .add("grid5", "./textures/polaroids/grid_3-1_polaroid.jpg")
        .add("grid6", "./textures/polaroids/grid_3-2_polaroid.jpg")
        .add("grid7", "./textures/polaroids/grid_4-1_polaroid.jpg")
        .add("grid8", "./textures/polaroids/grid_4-2_polaroid.jpg")
        .add("grid9", "./textures/polaroids/grid_5-1_polaroid.jpg")
        .add("grid10", "./textures/polaroids/grid_5-2_polaroid.jpg")
        .add("grid11", "./textures/polaroids/grid_6-1_polaroid.jpg")
        .add("grid12", "./textures/polaroids/grid_6-2_polaroid.jpg")
        .add("grid13", "./textures/polaroids/grid_7-1_polaroid.jpg")
        .add("grid14", "./textures/polaroids/grid_7-2_polaroid.jpg")
        .add("grid15", "./textures/polaroids/grid_8-1_polaroid.jpg")
        .add("grid16", "./textures/polaroids/grid_8-2_polaroid.jpg")
        .add("grid17", "./textures/polaroids/grid_9-1_polaroid.jpg")
        .add("grid18", "./textures/polaroids/grid_9-2_polaroid.jpg")
        .add("gridhex1", "./textures/hex/grid_1-1_polaroid.png")
        .add("gridhex2", "./textures/hex/grid_1-2_polaroid.png")
        .add("gridhex3", "./textures/hex/grid_2-1_polaroid.png")
        .add("gridhex4", "./textures/hex/grid_2-2_polaroid.png")
        .add("gridhex5", "./textures/hex/grid_3-1_polaroid.png")
        .add("gridhex6", "./textures/hex/grid_3-2_polaroid.png")
        .add("gridhex7", "./textures/hex/grid_4-1_polaroid.png")
        .add("gridhex8", "./textures/hex/grid_4-2_polaroid.png")
        .add("gridhex9", "./textures/hex/grid_5-1_polaroid.png")
        .add("gridhex10", "./textures/hex/grid_5-2_polaroid.png")
        .add("gridhex11", "./textures/hex/grid_6-1_polaroid.png")
        .add("gridhex12", "./textures/hex/grid_6-2_polaroid.png")
        .add("gridhex13", "./textures/hex/grid_7-1_polaroid.png")
        .add("gridhex14", "./textures/hex/grid_7-2_polaroid.png")
        .add("gridhex15", "./textures/hex/grid_8-1_polaroid.png")
        .add("gridhex16", "./textures/hex/grid_8-2_polaroid.png")
        .add("gridhex17", "./textures/hex/grid_9-1_polaroid.png")
        .add("gridhex18", "./textures/hex/grid_9-2_polaroid.png")
        .load(() => init(this.$refs["canvas-container"]));
    } else {
      PIXI.Loader.shared
        .add("grid1", "./textures/polaroids/grid_1-1_polaroid@2x.jpg")
        .add("grid2", "./textures/polaroids/grid_1-2_polaroid@2x.jpg")
        .add("grid3", "./textures/polaroids/grid_2-1_polaroid@2x.jpg")
        .add("grid4", "./textures/polaroids/grid_2-2_polaroid@2x.jpg")
        .add("grid5", "./textures/polaroids/grid_3-1_polaroid@2x.jpg")
        .add("grid6", "./textures/polaroids/grid_3-2_polaroid@2x.jpg")
        .add("grid7", "./textures/polaroids/grid_4-1_polaroid@2x.jpg")
        .add("grid8", "./textures/polaroids/grid_4-2_polaroid@2x.jpg")
        .add("grid9", "./textures/polaroids/grid_5-1_polaroid@2x.jpg")
        .add("grid10", "./textures/polaroids/grid_5-2_polaroid@2x.jpg")
        .add("grid11", "./textures/polaroids/grid_6-1_polaroid@2x.jpg")
        .add("grid12", "./textures/polaroids/grid_6-2_polaroid@2x.jpg")
        .add("grid13", "./textures/polaroids/grid_7-1_polaroid@2x.jpg")
        .add("grid14", "./textures/polaroids/grid_7-2_polaroid@2x.jpg")
        .add("grid15", "./textures/polaroids/grid_8-1_polaroid@2x.jpg")
        .add("grid16", "./textures/polaroids/grid_8-2_polaroid@2x.jpg")
        .add("grid17", "./textures/polaroids/grid_9-1_polaroid@2x.jpg")
        .add("grid18", "./textures/polaroids/grid_9-2_polaroid@2x.jpg")
        .add("gridhex1", "./textures/hex/grid_1-1_polaroid@2x.png")
        .add("gridhex2", "./textures/hex/grid_1-2_polaroid@2x.png")
        .add("gridhex3", "./textures/hex/grid_2-1_polaroid@2x.png")
        .add("gridhex4", "./textures/hex/grid_2-2_polaroid@2x.png")
        .add("gridhex5", "./textures/hex/grid_3-1_polaroid@2x.png")
        .add("gridhex6", "./textures/hex/grid_3-2_polaroid@2x.png")
        .add("gridhex7", "./textures/hex/grid_4-1_polaroid@2x.png")
        .add("gridhex8", "./textures/hex/grid_4-2_polaroid@2x.png")
        .add("gridhex9", "./textures/hex/grid_5-1_polaroid@2x.png")
        .add("gridhex10", "./textures/hex/grid_5-2_polaroid@2x.png")
        .add("gridhex11", "./textures/hex/grid_6-1_polaroid@2x.png")
        .add("gridhex12", "./textures/hex/grid_6-2_polaroid@2x.png")
        .add("gridhex13", "./textures/hex/grid_7-1_polaroid@2x.png")
        .add("gridhex14", "./textures/hex/grid_7-2_polaroid@2x.png")
        .add("gridhex15", "./textures/hex/grid_8-1_polaroid@2x.png")
        .add("gridhex16", "./textures/hex/grid_8-2_polaroid@2x.png")
        .add("gridhex17", "./textures/hex/grid_9-1_polaroid@2x.png")
        .add("gridhex18", "./textures/hex/grid_9-2_polaroid@2x.png")
        .load(() => init(this.$refs["canvas-container"]));
    }
  },
};
</script>
